type PossibleValueTypes = string | number | null | undefined;
type ValueType = string | number;

// https://gist.github.com/ionurboz/51b505ee3281cd713747b4a84d69f434?permalink_comment_id=3485333#gistcomment-3485333
export function debounce(func: Function, wait: number, immediate?: boolean) {
  // 'private' variable for instance
  // The returned function will be able to reference this due to closure.
  // Each call to the returned function will share this common timer.
  let timeout: any;

  // Calling debounce returns a new anonymous function
  return function (this: any) {
    // reference the context and args for the setTimeout function
    let context = this;
    let args = arguments;

    // Should the function be called now? If immediate is true
    //   and not already in a timeout then the answer is: Yes
    let callNow = immediate && !timeout;

    // This is the basic debounce behaviour where you can call this
    //   function several times, but it will only execute once
    //   [before or after imposing a delay].
    //   Each time the returned function is called, the timer starts over.
    clearTimeout(timeout);

    // Set the new timeout
    timeout = setTimeout(function () {
      // Inside the timeout function, clear the timeout variable
      // which will let the next execution run when in 'immediate' mode
      timeout = null;

      // Check if the function already ran with the immediate flag
      if (!immediate) {
        // Call the original function with apply
        // apply lets you define the 'this' object as well as the arguments
        //    (both captured before setTimeout)
        func.apply(context, args);
      }
    }, wait);

    // Immediate mode and no wait timer? Execute the function..
    if (callNow) func.apply(context, args);
  };
}

// gist.github.com/ionurboz/51b505ee3281cd713747b4a84d69f434?permalink_comment_id=3479289#gistcomment-3479289
// Returns a function, that, when invoked, will only be triggered at most once
// during a given window of time. Normally, the throttled function will run
// as much as it can, without ever going more than once per `wait` duration;
// but if you'd like to disable the execution on the leading edge, pass
// `{leading: false}`. To disable execution on the trailing edge, ditto.
export function throttle(func: Function, wait: number, options: any) {
  let context: any;
  let args: any;
  let result: any;

  let timeout: any = null;
  let previous = 0;
  if (!options) options = {};
  let later = function () {
    previous = options.leading === false ? 0 : Date.now();
    timeout = null;
    result = func.apply(context, args);
    if (!timeout) context = args = null;
  };
  return function (this: any) {
    let now = Date.now();
    if (!previous && options.leading === false) previous = now;
    let remaining = wait - (now - previous);
    context = this;
    args = arguments;
    if (remaining <= 0 || remaining > wait) {
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
      previous = now;
      result = func.apply(context, args);
      if (!timeout) context = args = null;
    } else if (!timeout && options.trailing !== false) {
      timeout = setTimeout(later, remaining);
    }
    return result;
  };
}

/**
 * https://gist.github.com/jed/982883 (Gist with original code and discussion)
 *
 * Produces a UUID in the following format:
 *
 * @example
 * When given a placeholder:  uuid('foo') => '7-1692286094667'
 * When given no placeholder:  uuid() => '5b4a30e8-2de7-477f-12f5-a621d862a24a-1692286094667'
 *
 * Has been tested on several browsers and has produced no collisions up to 50 million uuids,
 * HOWEVER, some in browsers Math.random() has produced collisions after ~20 million itterations.
 *
 * For this reason, this implementation adds `Date.now()` to the end of all uuids to reduce the
 * chance for collisions
 *
 *
 *
 */

const buildUUID = (placeholder: string = "") =>
  placeholder
    ? /* eslint-disable no-bitwise */
      (
        (Number(placeholder) ^ (Math.random() * 16)) >>
        (Number(placeholder) / 4)
      ).toString(16)
    : `${1e7}-${1e3}-${4e3}-${8e3}-${1e11}`.replace(/[018]/g, buildUUID);

export const uuid = (placeholder: string = ""): string =>
  `${buildUUID(placeholder)}-${Date.now()}`;

export function cloneDeep<T>(value: T) {
  if (!window.structuredClone) {
    // eslint-disable-next-line no-inner-declarations
    function cloneDeepFallback(value: any) {
      if (Array.isArray(value)) {
        const count = value.length;
        const arr = new Array(count);
        for (let i = 0; i < count; i++) {
          arr[i] = cloneDeepFallback(value[i]);
        }

        return arr;
      } else if (typeof value === "object") {
        const obj: any = {};
        for (const prop in value) {
          obj[prop] = cloneDeepFallback(value[prop]);
        }

        return obj;
      } else {
        // Primitive value
        return value;
      }
    }

    window.structuredClone = cloneDeepFallback;
  }
  return structuredClone(value as T);
}

// https://stackoverflow.com/a/54733755
export const set = (obj: any, path: string | string[], value: any) => {
  if (Object(obj) !== obj) return obj;
  if (!Array.isArray(path)) path = path.toString().match(/[^.[\]]+/g) || [];
  (path as string[])
    .slice(0, -1)
    .reduce(
      (a, c, i) =>
        Object(a[c]) === a[c]
          ? a[c]
          : (a[c] =
              Math.abs(Number(path[i + 1])) >> 0 === +path[i + 1] ? [] : {}),
      obj
    )[path[path.length - 1]] = value;
  return obj;
};

export const groupObjectBy = function (xs: any, key: any) {
  return xs.reduce(function (rv: any, x: any) {
    const id = x[key];
    rv[id] = x;
    return rv;
  }, {});
};

export function groupArrayOfObjByKey<T extends object>(
  array: T[] = [],
  key: keyof T,
  keyFallBack?: keyof T
) {
  // @ts-ignore
  return array.reduce((hash: T, obj: T) => {
    const objKey = obj[key] || keyFallBack;

    // @ts-ignore
    return objKey
      ? Object.assign(hash, {
          // @ts-ignore
          [objKey as keyof T]: (hash[objKey] || []).concat(obj),
        })
      : {};
  }, {});
}

// Number assertions
export const isNumber = (value?: ValueType | null) =>
  !isNaN(parseFloat(value as string)) && !isNaN((value as number) - 0);

export const getURLSearchParams = (paramName: string): string[] | null => {
  const queryString = window.location.search;

  const queryParams = new URLSearchParams(queryString);

  if (!queryParams.has(paramName)) return null;

  const params = queryParams.getAll(paramName);
  return params.length ? params : null;
};

export function insertItemToArrayAtIndex(
  array: any[],
  index: number,
  item: any
) {
  return array.splice(index, 0, item);
}

export function removeItemFromArrayByIndex(array: any[], index: number) {
  return array.splice(index, 1);
}

export function updateItemInArrayAtIndex(
  array: any[],
  index: number,
  item: any
) {
  return array.splice(index, 1, item);
}

// https://stackoverflow.com/a/25539452
export function jsonToURI(value: any) {
  return encodeURIComponent(JSON.stringify(value));
}

export function uriToJSON(urijson: string) {
  return JSON.parse(decodeURIComponent(urijson));
}

export function isObject(value: any): value is any {
  if (typeof value !== "object" || value === null) return false;

  if (Object.prototype.toString.call(value) !== "[object Object]") return false;

  const proto = Object.getPrototypeOf(value);
  if (proto === null) return true;

  const objectConstructor =
    Object.prototype.hasOwnProperty.call(proto, "constructor") &&
    proto.constructor;
  return (
    typeof objectConstructor === "function" &&
    objectConstructor instanceof objectConstructor &&
    Function.prototype.call(objectConstructor) ===
      Function.prototype.call(value)
  );
}

// Generic assertions
export const checkForNull = <T extends PossibleValueTypes>(v: T) => v == null;

export function isUndefined(value: any): value is undefined {
  return typeof value === "undefined" || value === undefined;
}

// Array assertions
export function isArray<T>(value: any): value is Array<T> {
  return Array.isArray(value);
}

export function isEmptyArray<T>(value: any): value is Array<T> {
  if (isUndefined(value) || !isArray(value)) return false;
  return value.length === 0;
}

export function arrayHasLength<T>(value: any): value is Array<T> {
  if (isUndefined(value) || !isArray(value)) return false;
  return value.length > 0;
}

export const getZoneShortCodeAndZonePageCodeId = ({
  pageShortCode,
  zoneShortCode,
}: {
  pageShortCode: string;
  zoneShortCode: string;
}) => {
  if (!zoneShortCode) return "";

  return `${zoneShortCode}${pageShortCode ? `-${pageShortCode}` : ""}`;
};

export const getPageZoneSelector = ({
  pageShortCode,
  zoneShortCode,
}: {
  pageShortCode: string;
  zoneShortCode: string;
}) => {
  if (!zoneShortCode) return "";

  return `[data-zone-short-code="${zoneShortCode}"]${
    pageShortCode ? `[data-page-short-code="${pageShortCode || ""}"]` : ""
  }`;
};

export const getImageZoneElement = ({ element }: { element: HTMLElement }) => {
  let imageElement: HTMLImageElement | null = null;

  const isZoneElementImage = element.nodeName === "IMG";

  if (isZoneElementImage) {
    imageElement = element as HTMLImageElement;
  } else {
    let possibleChildImageElement = element.querySelector(`:scope img`);

    if (possibleChildImageElement?.nodeName === "IMG") {
      imageElement = possibleChildImageElement as HTMLImageElement;
    }
  }

  return imageElement;
};
